import { ApiError, isValidationError } from "@config/apiClient";
import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@config/queryClient";
import companyService from "@services/company.service";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES } from "@config/messages";
import { useCreateMeetingObject } from "@screens/meeting/hooks/useCreateMeetingObject";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import { ICreateCompanyFromExternal } from "@interfaces/company.interface";
import { RelationObjectType } from "@enums/relations.enum";
import {
  COMPANY_SUGGESTIONS_QUERY_KEY,
  CREATE_COMPANY_FROM_EXTERNAL_MUTATION_KEY,
} from "@config/query";

export function useCreateCompanyFromExternal(leftContentType: RelationObjectType) {
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const { mutateAsync: createMeetingObject } = useCreateMeetingObject();

  return useMutation({
    mutationKey: [CREATE_COMPANY_FROM_EXTERNAL_MUTATION_KEY],
    mutationFn: async (data: ICreateCompanyFromExternal) => {
      return await companyService.createCompanyFromExternal(data);
    },
    onSuccess: (response) => {
      if (leftContentType === RelationObjectType.MEETING) {
        void createMeetingObject({
          object_type: RelationObjectType.COMPANY,
          object_id: response.company.id,
        });
      }
      // remove company suggestions query to prevent showing it in dropdown right after creation
      queryClient.removeQueries({ queryKey: [COMPANY_SUGGESTIONS_QUERY_KEY] });

      ToastService.showSuccess(`"${response.company.title}" company created`);
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;
      if (!isValidationError(error)) notifyBugsnag(error, ERROR_STRINGS.CREATE_COMPANY);
      // A 400 carries the reason the user needs (e.g. a required attribute); the generic
      // message is only for failures with nothing readable to show.
      ToastService.showError(
        error as ApiError,
        isValidationError(error) ? undefined : ERROR_MESSAGES.CREATE_COMPANY
      );
    },
  });
}
